import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ShieldCheck, KeyRound, Trash2, Power, PowerOff, Edit3, Crown, Users } from "lucide-react";
import { PermissionMatrix } from "@/components/admin/PermissionMatrix";

const EMPTY = { nom: "", prenom: "", email: "", telephone: "", pag_role: "support", permissions: null, password: "" };

export default function AdminTeam() {
  const [catalog, setCatalog] = useState(null);
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode: "create"|"edit", form, account }
  const [created, setCreated] = useState(null);
  const [pwd, setPwd] = useState(null); // { account, result }

  const load = async () => {
    try {
      const [c, t] = await Promise.all([api.get("/admin/team/catalog"), api.get("/admin/team")]);
      setCatalog(c.data); setData(t.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const preset = (key) => catalog?.roles.find((r) => r.key === key)?.permissions || {};
  const openCreate = () => setDialog({ mode: "create", form: { ...EMPTY, permissions: preset("support") } });
  const openEdit = (a) => setDialog({ mode: "edit", account: a, form: { nom: a.nom, prenom: a.prenom, email: a.email || "", telephone: a.telephone || "", pag_role: a.pag_role, permissions: a.permissions || {} } });

  const save = async () => {
    const f = dialog.form;
    const body = { nom: f.nom, prenom: f.prenom, email: f.email || null, telephone: f.telephone || null, pag_role: f.pag_role, permissions: f.permissions || {} };
    try {
      if (dialog.mode === "create") {
        if (f.password) body.password = f.password;
        const { data: r } = await api.post("/admin/team", body);
        setCreated(r); setDialog(null);
      } else {
        await api.patch(`/admin/team/${dialog.account.id}`, body);
        toast.success("Compte mis à jour"); setDialog(null);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const roleLabel = (k) => catalog?.roles.find((r) => r.key === k)?.label || k;

  return (
    <div className="min-h-full" data-testid="admin-team-page">
      <PageHeader eyebrow="Équipe PAG" title="Comptes secondaires & permissions"
        sub="Un seul administrateur principal. Chaque compte secondaire reçoit un profil et une matrice Voir / Créer / Modifier / Valider / Supprimer / Exporter / Administrer, appliquée par le serveur sur chaque appel."
        right={<Button onClick={openCreate} data-testid="team-add-btn" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold"><Plus className="h-4 w-4 mr-2" /> Nouveau compte</Button>} />

      <div className="p-6 lg:p-10 space-y-6">
        {data && (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-[#0A0A0A] text-white p-5 border border-[#0A0A0A]" data-testid="team-principal">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#FFD700]"><Crown className="h-3 w-3" /> Admin principal</div>
              <div className="font-display text-2xl font-black mt-2">{data.principal_count} / 1</div>
              <div className="font-mono text-xs text-white/70 mt-1">{data.principal?.code} · {data.principal?.prenom} {data.principal?.nom}</div>
              <div className="text-[10px] text-white/50 mt-2">Aucun second administrateur principal ne peut être créé.</div>
            </div>
            <div className="bg-white p-5 border border-[#DEE2E6]" data-testid="team-count">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]"><Users className="h-3 w-3" /> Comptes secondaires</div>
              <div className="font-display text-2xl font-black mt-2">{data.staff.length}</div>
              <div className="text-[10px] text-[#868E96] mt-1">{data.staff.filter((s) => s.active).length} actifs</div>
            </div>
            <div className="bg-white p-5 border border-[#DEE2E6]" data-testid="team-roles">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]"><ShieldCheck className="h-3 w-3" /> Profils disponibles</div>
              <div className="flex flex-wrap gap-1 mt-2">{catalog?.roles.map((r) => <span key={r.key} className="text-[10px] font-bold uppercase tracking-wider border border-[#DEE2E6] px-1.5 py-0.5">{r.label}</span>)}</div>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#DEE2E6]" data-testid="team-list">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-3">Compte</div><div className="col-span-2">Profil</div><div className="col-span-4">Modules autorisés</div><div className="col-span-1">Statut</div><div className="col-span-2 text-right">Actions</div>
          </div>
          {!data || data.staff.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]" data-testid="team-empty">Aucun compte secondaire. Créez un compte Helper, Support, Commercial, Finance, QualiParc, Technique ou Développeur.</div>
          ) : data.staff.map((a) => (
            <div key={a.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-[#F1F3F5] last:border-b-0 text-sm" data-testid={`team-row-${a.code}`}>
              <div className="col-span-3"><div className="font-semibold">{a.prenom} {a.nom}</div><div className="font-mono text-[11px] text-[#868E96]">{a.code}{a.email ? ` · ${a.email}` : ""}</div></div>
              <div className="col-span-2"><span className="text-[10px] font-bold uppercase tracking-wider bg-[#F8F9FA] border border-[#DEE2E6] px-1.5 py-0.5">{roleLabel(a.pag_role)}</span></div>
              <div className="col-span-4 flex flex-wrap gap-1" data-testid={`team-modules-${a.code}`}>
                {Object.entries(a.permissions || {}).map(([m, acts]) => <span key={m} className="text-[10px] border border-[#002FA7]/30 text-[#002FA7] px-1.5 py-0.5" title={acts.join(", ")}>{catalog?.modules.find((x) => x.key === m)?.label.split(" (")[0] || m} · {acts.length}</span>)}
                {!Object.keys(a.permissions || {}).length && <span className="text-[#868E96] italic text-xs">Aucune permission</span>}
              </div>
              <div className="col-span-1"><span className={`status-pill ${a.active ? "status-active" : "status-inactive"}`}>{a.active ? "Actif" : "Désactivé"}</span></div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`team-edit-${a.code}`} className="h-8 px-2"><Edit3 className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => setPwd({ account: a, result: null })} data-testid={`team-pwd-${a.code}`} className="h-8 px-2 border-[#FFD700] hover:bg-[#FFD700]"><KeyRound className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => act(() => api.patch(`/admin/team/${a.id}`, { active: !a.active }), a.active ? "Compte désactivé (sessions coupées)" : "Compte réactivé")} data-testid={`team-toggle-${a.code}`} className="h-8 px-2">{a.active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}</Button>
                <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`Supprimer le compte ${a.code} ?`)) act(() => api.delete(`/admin/team/${a.id}`), "Compte supprimé"); }} data-testid={`team-delete-${a.code}`} className="h-8 px-2 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dialog création / édition */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">{dialog?.mode === "create" ? "Nouveau compte secondaire" : `Modifier ${dialog?.account?.code}`}</DialogTitle></DialogHeader>
          {dialog && catalog && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <F label="Prénom *" value={dialog.form.prenom} onChange={(v) => setDialog({ ...dialog, form: { ...dialog.form, prenom: v } })} testid="team-prenom" />
                <F label="Nom *" value={dialog.form.nom} onChange={(v) => setDialog({ ...dialog, form: { ...dialog.form, nom: v } })} testid="team-nom" />
                <F label="Email" type="email" value={dialog.form.email} onChange={(v) => setDialog({ ...dialog, form: { ...dialog.form, email: v } })} testid="team-email" />
                <F label="Téléphone" value={dialog.form.telephone} onChange={(v) => setDialog({ ...dialog, form: { ...dialog.form, telephone: v } })} testid="team-tel" />
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Profil</Label>
                  <Select value={dialog.form.pag_role} onValueChange={(v) => setDialog({ ...dialog, form: { ...dialog.form, pag_role: v, permissions: preset(v) } })}>
                    <SelectTrigger data-testid="team-role" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{catalog.roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="text-[11px] text-[#495057]" data-testid="team-role-desc">{catalog.roles.find((r) => r.key === dialog.form.pag_role)?.desc}</div>
                </div>
                {dialog.mode === "create" && <F label="Mot de passe (vide = généré, 8 car. min)" type="text" value={dialog.form.password} onChange={(v) => setDialog({ ...dialog, form: { ...dialog.form, password: v } })} testid="team-password" />}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Centre de permissions</Label>
                  <button type="button" onClick={() => setDialog({ ...dialog, form: { ...dialog.form, permissions: preset(dialog.form.pag_role) } })} className="text-[11px] font-semibold text-[#002FA7] hover:underline" data-testid="team-reset-preset">Réinitialiser au profil</button>
                </div>
                <PermissionMatrix modules={catalog.modules} actions={catalog.actions} value={dialog.form.permissions} onChange={(p) => setDialog({ ...dialog, form: { ...dialog.form, permissions: p } })} />
                <div className="text-[11px] text-[#868E96] mt-2">Cliquer sur un nom de module coche / décoche toute la ligne. Toute action implique « Voir ». « Équipe PAG » reste réservée à l'administrateur principal.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Annuler</Button>
            <Button onClick={save} disabled={!dialog?.form.nom.trim() || !dialog?.form.prenom.trim()} data-testid="team-save" className="bg-[#002FA7] hover:bg-[#002175] text-white">{dialog?.mode === "create" ? "Créer le compte" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Identifiants créés */}
      <Dialog open={!!created} onOpenChange={(o) => { if (!o) setCreated(null); }}>
        <DialogContent data-testid="team-created-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">Compte créé</DialogTitle></DialogHeader>
          {created && (
            <div className="space-y-3">
              <div className="p-4 bg-[#ECFDF5] border border-[#A7F3D0] text-sm">Transmettez ces identifiants de façon sécurisée. Le mot de passe n'est plus affiché ensuite.</div>
              <div className="p-4 border border-[#DEE2E6] bg-[#F8F9FA] font-mono text-sm space-y-1" data-testid="team-created-info">
                <div><span className="text-[#868E96]">Code :</span> <b>{created.code}</b></div>
                <div><span className="text-[#868E96]">Mot de passe :</span> <b>{created.password}</b></div>
                <div><span className="text-[#868E96]">Connexion :</span> /login/admin</div>
              </div>
              <Button onClick={() => setCreated(null)} className="w-full bg-[#0A0A0A] text-white" data-testid="team-created-close">Terminer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset mot de passe */}
      <Dialog open={!!pwd} onOpenChange={(o) => { if (!o) setPwd(null); }}>
        <DialogContent data-testid="team-pwd-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#FFD700]" /> Nouveau mot de passe</DialogTitle></DialogHeader>
          {pwd && !pwd.result && (
            <div className="space-y-3">
              <div className="text-sm">{pwd.account.prenom} {pwd.account.nom} · <span className="font-mono">{pwd.account.code}</span></div>
              <div className="text-xs text-[#495057]">Un mot de passe aléatoire sera généré et toutes les sessions ouvertes seront coupées.</div>
              <Button onClick={async () => { try { const { data: r } = await api.post(`/admin/team/${pwd.account.id}/password`, {}); setPwd({ ...pwd, result: r }); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} data-testid="team-pwd-generate" className="w-full bg-[#002FA7] text-white">Générer</Button>
            </div>
          )}
          {pwd?.result && (
            <div className="space-y-3" data-testid="team-pwd-result">
              <div className="p-4 border border-[#DEE2E6] bg-[#F8F9FA] font-mono text-sm space-y-1">
                <div><span className="text-[#868E96]">Code :</span> <b>{pwd.result.code}</b></div>
                <div><span className="text-[#868E96]">Mot de passe :</span> <b>{pwd.result.password}</b></div>
              </div>
              <Button onClick={() => setPwd(null)} className="w-full bg-[#0A0A0A] text-white">Fermer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-11" />
    </div>
  );
}
